import React from 'react';
import { ExecutionResult, fileApi } from '../services/api';
import QAValidationResults from './QAValidationResults';
import './ResultsView.css';

interface ResultsViewProps {
  result: ExecutionResult;
  onFixWithAssertion?: (error: string) => void;
  onShowHistory?: () => void;
}

const ResultsView: React.FC<ResultsViewProps> = ({ result, onFixWithAssertion, onShowHistory }) => {
  const downloadResult = () => {
    if (result.result_file_id) {
      console.log('Downloading file with ID:', result.result_file_id);
      const downloadUrl = fileApi.download(result.result_file_id);
      console.log('Download URL:', downloadUrl);
      window.open(downloadUrl, '_blank');
    }
  };

  const formatExecutionTime = (seconds: number) => {
    if (seconds < 1) {
      return `${(seconds * 1000).toFixed(0)}ms`;
    }
    return `${seconds.toFixed(2)}s`;
  };

  return (
    <div className="results-view-container">
      <div className={`execution-status ${result.success ? 'success' : 'error'}`}>
        <div className="status-icon">
          {result.success ? '✓' : '✗'}
        </div>
        <div className="status-text">
          {result.success ? 'Execution Successful' : 'Execution Failed'}
        </div>
      </div>

      {/* Stale Results Warning */}
      {result.script_version && result.current_script_version && 
       result.script_version < result.current_script_version && (
        <div className="stale-warning">
          <div className="warning-icon">⚠️</div>
          <div className="warning-content">
            <div className="warning-title">These results may be outdated</div>
            <div className="warning-message">
              The script has been modified since this execution (v{result.script_version} → v{result.current_script_version}).
              Consider re-running the script to get updated results.
            </div>
          </div>
        </div>
      )}

      <div className="execution-details">
        <div className="detail-item">
          <span className="detail-label">Execution Time:</span>
          <span className="detail-value">{formatExecutionTime(result.execution_time)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Executed At:</span>
          <span className="detail-value">
            {new Date(result.executed_at).toLocaleString()}
          </span>
        </div>
        {result.script_version && (
          <div className="detail-item">
            <span className="detail-label">Script Version:</span>
            <span className="detail-value">v{result.script_version}</span>
          </div>
        )}
        {result.input_file_names && result.input_file_names.length > 0 && (
          <div className="detail-item">
            <span className="detail-label">Input Files:</span>
            <span className="detail-value">
              {result.input_file_names.join(', ')}
            </span>
          </div>
        )}
        {onShowHistory && (
          <div className="detail-item">
            <button 
              onClick={onShowHistory}
              className="history-link"
              style={{
                background: 'none',
                border: 'none',
                color: '#FF6600',
                cursor: 'pointer',
                padding: 0,
                fontSize: '13px',
                textDecoration: 'underline'
              }}
            >
              View execution history
            </button>
          </div>
        )}
      </div>

      {result.output && (
        <div className="output-section">
          <h4>Output</h4>
          <pre className="output-content">{result.output}</pre>
        </div>
      )}

      {result.error && (
        <div className="error-section">
          <h4>Error</h4>
          <pre className="error-content">{result.error}</pre>
          {onFixWithAssertion && (
            <div style={{ 
              marginTop: '16px', 
              borderTop: '1px solid #e0e0e0', 
              paddingTop: '16px' 
            }}>
              <p style={{ 
                fontSize: '13px', 
                color: '#666', 
                marginBottom: '8px' 
              }}>
                Need help fixing this error? Let Assertion analyze and fix it for you.
              </p>
              <button 
                onClick={() => onFixWithAssertion(result.error!)}
                className="fix-with-assertion-button"
                style={{
                  backgroundColor: '#FF6600',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(255, 102, 0, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#E55A00';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(255, 102, 0, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#FF6600';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(255, 102, 0, 0.2)';
                }}
              >
                <span>🤖</span>
                <span>Fix with Assertion</span>
              </button>
            </div>
          )}
        </div>
      )}

      {(result.result_file_id || (result.output_files && result.output_files.length > 0)) && (
        <div className="download-section">
          <h4>Result Files</h4>
          <p>Your output files are ready for download.</p>
          {result.output_file_details && result.output_file_details.length > 0 ? (
            <div>
              {result.output_file_details.map((file, index) => (
                <button 
                  key={index}
                  onClick={() => {
                    const downloadUrl = fileApi.download(file.file_id);
                    window.open(downloadUrl, '_blank');
                  }} 
                  className="download-button"
                  style={{ marginBottom: '10px', display: 'block' }}
                >
                  Download {file.filename}
                </button>
              ))}
            </div>
          ) : (
            <button onClick={downloadResult} className="download-button">
              Download Result File
            </button>
          )}
        </div>
      )}

      {!result.result_file_id && result.success && (
        <div className="warning-section">
          <p>⚠️ No output file was generated. Make sure your script saves the results to a CSV or Excel file.</p>
        </div>
      )}

      {result.qa_validation && (
        <QAValidationResults validation={result.qa_validation} />
      )}
    </div>
  );
};

export default ResultsView;